import type { CSSObject } from '@balance-web/core';
import type { BalanceTheme } from '@balance-web/theme';
import { FOCUS_RING } from '@balance-web/theme';

export type ColorScheme = 'primary' | 'secondary' | 'tertiary' | 'critical';
export type Variant = 'filled' | 'outline' | 'text';
export type Size = 'xsmall' | 'small' | 'medium' | 'large';

export type ButtonStylesProps = {
  colorScheme: ColorScheme;
  variant: Variant;
  disabled?: boolean;
  loading?: boolean;
  block: boolean;
  hasIcon: boolean;
  isIcon: boolean;
  size: Size;

  fontWeight?: number;
  borderRadius?: CSSObject['borderRadius'];
  borderLeftWidth?: CSSObject['borderLeftWidth'];
  borderTopWidth?: CSSObject['borderTopWidth'];
  borderRightWidth?: CSSObject['borderRightWidth'];
  borderBottomWidth?: CSSObject['borderBottomWidth'];
};

type ButtonStyles = {
  borderStyles: CSSObject;
  focusedOverlay: CSSObject;
  hoverOverlay: CSSObject;
  pressedOverlay: CSSObject;
  root: CSSObject;
};

export const getButtonStyles = (
  {
    colorScheme,
    variant,
    block = false,
    borderRadius,
    borderLeftWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    fontWeight,
    size,
    disabled,
    hasIcon,
    isIcon,
  }: ButtonStylesProps,
  theme: BalanceTheme
): ButtonStyles => {
  let paddingBlock = '0';
  let paddingInline = '0';

  // Annoying tiny adjustments because padding multiples + font size is always off by a few pixel.
  // Can't hardcode height because OS font size can become inaccessible.
  if (isIcon) {
    if (size === 'xsmall') {
      paddingBlock = theme.spacing.xsmall;
      paddingInline = theme.spacing.xsmall;
    }
    if (size === 'small') {
      paddingBlock = theme.spacing.small;
      paddingInline = theme.spacing.small;
    } else if (size === 'medium') {
      paddingBlock = theme.spacing.small;
      paddingInline = theme.spacing.small;
    } else if (size === 'large') {
      paddingBlock = theme.spacing.medium;
      paddingInline = theme.spacing.medium;
    }
  } else {
    paddingInline = theme.spacing[size === 'large' ? 'xlarge' : 'large'];

    if (hasIcon && size === 'small') {
      paddingBlock = theme.spacing.small;
    } else if (hasIcon && size === 'medium') {
      paddingBlock = theme.spacing.small;
    } else if (hasIcon && size === 'large') {
      paddingBlock = theme.spacing.small;
    } else if (size === 'xsmall') {
      paddingBlock = '0';
    } else if (size === 'small') {
      paddingBlock = `calc(${theme.spacing.small} + 1px)`;
    } else if (size === 'medium') {
      paddingBlock = theme.spacing.medium;
    } else if (size === 'large') {
      paddingBlock = `calc(${theme.spacing.large} - 1px)`;
    }
  }

  const baseStyles: CSSObject = {
    position: 'relative',
    alignItems: 'center',
    backgroundColor: 'white',
    border: 'none',
    borderRadius,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: block ? 'flex' : 'inline-flex',
    gap: theme.spacing.small,
    flexShrink: 0, // button text should NOT wrap, even within a flex container
    fontFamily: theme.typography.fontFamily.body,
    fontWeight,
    justifyContent: 'center',
    outline: 0,
    textDecoration: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    width: block ? '100%' : undefined,

    // size properties
    fontSize: theme.typography.fontSize[size],
    paddingInline,
    paddingBlock,

    // cover all cases by desaturating and lowering opacity
    '&[aria-disabled=true]': {
      cursor: 'default',
    },

    '&:not([aria-disabled=true]):focus-visible': {
      boxShadow: `0 0 0 2px ${FOCUS_RING}`,
    },
  };

  const colors = {
    primary: {
      filled: {
        background: theme.semantic.colour.primary.background,
        border: theme.semantic.colour.primary.background,
        text: theme.semantic.colour.primary.foreground,
        hover: {
          background: theme.semantic.colour.primary.interactions.hover,
        },
        focused: {
          background: theme.semantic.colour.primary.interactions.focused,
        },
        pressed: {
          background: theme.semantic.colour.primary.interactions.pressed,
        },
      },
      outline: {
        background: theme.semantic.colour.primary.inverse.background,
        border: theme.semantic.colour.primary.inverse.foreground,
        text: theme.semantic.colour.primary.inverse.foreground,
        hover: {
          background: theme.semantic.colour.primary.inverse.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.primary.inverse.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.primary.inverse.interactions.pressed,
        },
      },
      text: {
        background: 'none',
        border: undefined,
        text: theme.semantic.colour.primary.inverse.foreground,
        hover: {
          background: theme.semantic.colour.primary.inverse.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.primary.inverse.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.primary.inverse.interactions.pressed,
        },
      },
    },
    secondary: {
      filled: {
        background: theme.semantic.colour.primary.container.background,
        border: theme.semantic.colour.secondary.border,
        text: theme.semantic.colour.text.textPrimary,
        hover: {
          background: theme.semantic.colour.secondary.interactions.hover,
        },
        focused: {
          background: theme.semantic.colour.secondary.interactions.focused,
        },
        pressed: {
          background: theme.semantic.colour.primary.interactions.pressed,
        },
      },
      outline: {
        background: theme.semantic.colour.neutral.background,
        border: theme.semantic.colour.secondary.border,
        text: theme.semantic.colour.text.textPrimary,
        hover: {
          background:
            theme.semantic.colour.secondary.inverse.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.secondary.inverse.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.secondary.inverse.interactions.pressed,
        },
      },
      text: {
        background: 'none',
        border: undefined,
        text: theme.semantic.colour.text.textPrimary,
        hover: {
          background:
            theme.semantic.colour.secondary.inverse.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.secondary.inverse.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.secondary.inverse.interactions.pressed,
        },
      },
    },
    tertiary: {
      filled: {
        background: theme.semantic.colour.surface.primary.background1,
        border: theme.semantic.colour.neutral.border,
        text: theme.semantic.colour.neutral.container.foreground,
        hover: {
          background:
            theme.semantic.colour.neutral.container.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.neutral.container.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.neutral.container.interactions.pressed,
        },
      },
      outline: {
        background: theme.semantic.colour.neutral.background,
        border: theme.semantic.colour.neutral.border,
        text: theme.semantic.colour.neutral.container.foreground,
        hover: {
          background:
            theme.semantic.colour.neutral.container.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.neutral.container.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.neutral.container.interactions.pressed,
        },
      },
      text: {
        background: 'none',
        border: undefined,
        text: theme.semantic.colour.neutral.container.foreground,
        hover: {
          background:
            theme.semantic.colour.neutral.container.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.neutral.container.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.neutral.container.interactions.pressed,
        },
      },
    },
    critical: {
      filled: {
        background: theme.semantic.colour.reserved.critical.background,
        border: theme.semantic.colour.reserved.critical.background,
        text: theme.semantic.colour.reserved.critical.foreground,
        hover: {
          background:
            theme.semantic.colour.reserved.critical.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.reserved.critical.interactions.focused,
        },
        pressed: {
          background:
            theme.semantic.colour.reserved.critical.interactions.pressed,
        },
      },
      outline: {
        background: theme.semantic.colour.reserved.critical.inverse.background,
        border: theme.semantic.colour.reserved.critical.inverse.foreground,
        text: theme.semantic.colour.reserved.critical.inverse.foreground,
        hover: {
          background:
            theme.semantic.colour.reserved.critical.inverse.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.reserved.critical.inverse.interactions
              .focused,
        },
        pressed: {
          background:
            theme.semantic.colour.reserved.critical.inverse.interactions
              .pressed,
        },
      },
      text: {
        background: 'none',
        border: undefined,
        text: theme.semantic.colour.reserved.critical.inverse.foreground,
        hover: {
          background:
            theme.semantic.colour.reserved.critical.inverse.interactions.hover,
        },
        focused: {
          background:
            theme.semantic.colour.reserved.critical.inverse.interactions
              .focused,
        },
        pressed: {
          background:
            theme.semantic.colour.reserved.critical.inverse.interactions
              .pressed,
        },
      },
    },
    disabled: {
      filled: {
        background: theme.semantic.colour.neutral.inverse.interactions.hover,
        border: theme.semantic.colour.neutral.inverse.interactions.hover,
        text: theme.semantic.colour.text.textDisabled,
      },
      outline: {
        background: theme.semantic.colour.neutral.inverse.interactions.hover,
        border: theme.semantic.colour.neutral.inverse.interactions.hover,
        text: theme.semantic.colour.text.textDisabled,
      },
      text: {
        background: 'none',
        border: undefined,
        text: theme.semantic.colour.text.textDisabled,
      },
    },
  };

  const borderStyles: CSSObject =
    variant === 'text'
      ? {}
      : // We use pseudo elements for border because we need to be able to turn borders on/off on each side without affect hide/width of the element.
        {
          '&:before': {
            position: 'absolute',
            content: '" "',
            display: 'flex',
            border: `${theme.borderWidth.standard} solid ${
              disabled
                ? colors.disabled[variant].border
                : colors[colorScheme][variant].border
            }`,
            borderRadius,
            borderLeftWidth,
            borderTopWidth,
            borderRightWidth,
            borderBottomWidth,
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          },
        };

  const rootStyles: CSSObject = {
    ...baseStyles,
    ...borderStyles,
    background: disabled
      ? colors.disabled[variant].background
      : colors[colorScheme][variant].background,
    color: disabled
      ? colors.disabled[variant].text
      : colors[colorScheme][variant].text,
    position: 'relative',
    borderRadius,
    '&:not([aria-disabled=true])': {
      '&:hover': {
        '> .hover-overlay': {
          opacity: 1,
          pointerEvents: 'all',
        },
      },
      '&:focus': {
        '> .focused-overlay': {
          opacity: 1,
          pointerEvents: 'all',
        },
        '> .hover-overlay': {
          opacity: 0,
          pointerEvents: 'none',
        },
      },
      '&:active': {
        transform: 'translateY(1px)',
        '> .pressed-overlay': {
          opacity: 1,
          pointerEvents: 'all',
        },
        '> .hover-overlay': {
          opacity: 0,
          pointerEvents: 'none',
        },
        '> .focused-overlay': {
          opacity: 0,
          pointerEvents: 'none',
        },
      },
    },
  };

  return {
    root: rootStyles,
    borderStyles,
    hoverOverlay: {
      borderRadius,
      background: colors[colorScheme][variant].hover.background,
      transition: 'opacity 200ms linear',
    },
    pressedOverlay: {
      borderRadius,
      background: colors[colorScheme][variant].pressed.background,
      transition: 'opacity 200ms linear',
    },
    focusedOverlay: {
      borderRadius,
      background: colors[colorScheme][variant].focused.background,
      transition: 'opacity 200ms linear',
    },
  };
};
